import { createTheme } from '@mui/material/styles';

export const colors = {
  black: '#000',
  mainBlue: '#00819E',
  mainGreen: '#03B2AB',
  mainGreenLight: 'rgba(3, 178, 171, 0.2)', // mainGreen at .2 opacity
  teal: '#E4F6F6',
  hotTeal: '#00A3FF',
  emptyField: '#919191',
  meetingSpaces: '#40B6F9',
  individualWorkspaces: '#A967E8',
  amenitySpaces: '#F58D8D',
  supportSpaces: '#6FCF97',
  sharedSpaces: '#959595',
  grayDivider: 'rgba(0,0,0,0.12)',
  grayLight: '#F2F2F2',
  grayMedium: '#CBCBCB',
  mainGray: '#707070',
  mainGrayAt60: 'rgba(112, 112, 112, 0.6)',
  greyInputAddornment: '#919191',
  darkText: '#4F4F4F',
  noColor: 'rgba(0,0,0,0)',
  brandDarkGreen: '#029D7C',
  white: '#FFF',
  offWhite: '#E0E0E0',
  dark: '#333333',
  fontColor: '#4A4A4A',
  shadowedSteel: '#4A4A4A',
  disabledCheckbox: 'rgba(0,0,0,0.54)',
  backgroundGrey: '#eee',
  containerBorder: '#E0E0E0',
  darkGray: '#4B4B4B',
  veryLightGray: '#DFDFDF',
  red: '#F01346',
  whiteSmoke: '#F5F5F5',
  lightGreyBackground: '#F7F7F7',
  lightGreen: '#02C39A',
  disabledIcons: '#959595',
  cautionDark: '#FF9800',
  statusIndicatorsBackground: '#E6F7F7',
  floorPlan: {
    // CHECK IN FIGMA BEFORE CHANGING THESE VALUES
    individual: '#a967e8',
    meeting: '#40B6F9',
    amenity: '#F58D8D',
    support: '#6FCF97',
    other: '#909090',
  },
  purples: [
    '#D7B8F4',
    '#C395EF',
    '#A967E8',
    '#9C4EE4',
    '#882ADF',
    '#731DC3',
    '#5E18A0',
    '#3F106A',
  ],
  spaceCategoriesOther: '#999999',
  borderBottomGray: '#e4e4e4',
  darkerGray: '#909090',
  secondaryGreen: '#03a099',
  secondaryRed: '#cf123e',
  midGray: '#666666',
  selectBorder: '#F5F5F5',
  inputBorderColor: 'rgba(0, 0, 0, 0.23)',
  hotPink: '#FF00B8',
  brightGray: '#EEEEEE',
  brandBlue: '#51C3C5',
  tableHeaderInnerBorderGray: '#A8A8A8',
  selectedListItem: '#00000014',
} as const;

export const theme = createTheme({
  palette: {
    error: {
      main: colors.red,
    },
    primary: {
      main: '#000000',
    },
    secondary: {
      main: '#000000',
    },
    action: {
      disabled: colors.mainGray,
    },
  },
  typography: {
    fontFamily: ['Roboto', 'sans-serif'].join(','),
  },
  components: {
    // Global update to material UI container component
    // Figma designs max width is 1280px but we're setting it to 1440px to leverage larger screens
    MuiButtonBase: {
      defaultProps: {
        // The props to change the default for.
        disableRipple: true, // No more ripple, on the whole application 💣!
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '&.MuiContainer-maxWidthLg': {
            maxWidth: '1440px',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: colors.disabledIcons,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 8 * 6,
          '&$expanded': {
            minHeight: 8 * 6,
          },
        },
        content: {
          margin: '12px 0',
          '&$expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2))',
          boxShadow: 'none',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: colors.veryLightGray,
          '&.Mui-checked': {
            transform: 'translateX(21px)',
            '&&:hover': {
              backgroundColor: 'unset',
            },
          },
        },
        thumb: {
          backgroundColor: colors.grayMedium,
          '.MuiSwitch-switchBase.Mui-checked &': {
            backgroundColor: colors.darkGray,
          },
        },
        track: {
          backgroundColor: colors.veryLightGray,
          opacity: 1,
          '.MuiSwitch-switchBase.Mui-checked + &': {
            opacity: 1,
          },
          '.MuiSwitch-colorSecondary.Mui-checked + &': {
            backgroundColor: colors.veryLightGray,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: colors.black,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          opacity: 0.7,
          '&.Mui-selected': {
            opacity: 1.0,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          lineHeight: 1,
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75)',
          },
        },
      },
    },
  },
});
